import React, { useEffect, useRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGalleryGestures } from '../../hooks/useGalleryGestures';
import { useBackToClose } from '../../hooks/useBackToClose';

function GalleryThumbs({ images, idx, onSelect }) {
  const refs = useRef([]);
  useEffect(() => {
    refs.current[idx]?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [idx]);
  if (images.length < 2) return null;
  return (
    <div className="flex gap-2 overflow-x-auto px-4 py-3 justify-start sm:justify-center [scrollbar-width:none]" data-testid="gallery-thumbs">
      {images.map((src, i) => (
        <button
          key={src + i}
          ref={(el) => { refs.current[i] = el; }}
          onClick={() => onSelect(i)}
          data-testid={`gallery-thumb-${i}`}
          className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-[opacity,border-color] duration-200 ${i === idx ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-80'}`}
        >
          <img src={src} alt="" loading="lazy" draggable="false" className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
}

function NavArrow({ dir, onClick, label }) {
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      onClick={onClick}
      aria-label={label}
      data-testid={`gallery-${dir}`}
      className={`hidden sm:flex absolute top-1/2 -translate-y-1/2 ${dir === 'prev' ? 'left-4' : 'right-4'} w-11 h-11 items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors`}
    >
      <Icon size={22} />
    </button>
  );
}

export function FullscreenGallery({ images, idx, setIdx, open, onOpenChange, title }) {
  const { t } = useTranslation();
  const stageRef = useRef(null);
  const close = () => onOpenChange(false);
  useBackToClose(open, onOpenChange);
  const count = images.length;
  const { view, drag, live, handlers } = useGalleryGestures({ count, idx, setIdx, onClose: close, stageRef });
  const go = (d) => { const n = idx + d; if (n >= 0 && n < count) setIdx(n); };
  const fade = Math.min(drag.y / 400, 0.7);
  const ease = live ? 'none' : 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)';

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black" style={{ opacity: 1 - fade }} />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onKeyDown={(e) => { if (e.key === 'ArrowLeft') go(-1); if (e.key === 'ArrowRight') go(1); }}
          className="fixed inset-0 z-[60] flex flex-col text-white outline-none select-none data-[state=open]:animate-in data-[state=open]:fade-in-0 duration-200"
          style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
          data-testid="fullscreen-gallery"
        >
          <DialogPrimitive.Title className="sr-only">{title || t('galleryTitle')}</DialogPrimitive.Title>
          <div className="flex items-center justify-between px-4 h-14 shrink-0" style={{ opacity: 1 - fade * 1.5 }}>
            <span className="text-sm font-medium tabular-nums" data-testid="gallery-counter">{idx + 1} / {count}</span>
            <button onClick={close} aria-label={t('galleryClose')} data-testid="gallery-close"
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 transition-colors">
              <X size={20} />
            </button>
          </div>
          <div ref={stageRef} {...handlers} className="relative flex-1 overflow-hidden" style={{ touchAction: 'none' }} data-testid="gallery-stage">
            <div
              className="flex h-full"
              style={{
                transform: `translate3d(calc(${-idx * 100}% + ${drag.x}px), ${drag.y}px, 0) scale(${1 - drag.y / 1500})`,
                transition: ease,
              }}
            >
              {images.map((src, i) => (
                <div key={src + i} className="w-full h-full shrink-0 flex items-center justify-center overflow-hidden">
                  {Math.abs(i - idx) <= 1 && (
                    <img
                      src={src}
                      alt=""
                      draggable="false"
                      data-testid={i === idx ? 'gallery-current-image' : undefined}
                      className="max-w-full max-h-full object-contain pointer-events-none"
                      style={i === idx ? { transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.s})`, transition: ease } : undefined}
                    />
                  )}
                </div>
              ))}
            </div>
            {idx > 0 && <NavArrow dir="prev" onClick={() => go(-1)} label={t('galleryPrev')} />}
            {idx < count - 1 && <NavArrow dir="next" onClick={() => go(1)} label={t('galleryNext')} />}
          </div>
          <div style={{ opacity: 1 - fade * 1.5 }}>
            <GalleryThumbs images={images} idx={idx} onSelect={setIdx} />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
